<template>
  <b-modal :ref="ref" :no-close-on-backdrop="true" :title="title">
    <div class="form-info">
      <div class="col">
        <div>
          <label>{{ $t('labels.lots.position_or_cost') }}</label>
          <multiselect v-model="evaluationData.purchase_order_item" :max-height="200"
                       placeholder="" label="description_full" track-by="id" :allow-empty="false"
                       :options="lots" :close-on-select="true" @input="lotChanged"
                       select-label="" deselect-label="" selected-label=""
                       :class="{required: !this.evaluationData.purchase_order_item}"
          />
        </div>
        <div>
          <label>{{ $t('labels.common.actual_price') }}, {{ priceInfo }}</label>
          <input class="form-control" v-model.number="evaluationData.price_per_unit" disabled/>
        </div>
        <div class="d-flex justify-content-between">
          <div class="flex-grow-1 mr-2">
            <label>{{ $t('labels.common.defect_value') }}, {{ evaluationData.penalty_measure }}</label>
            <input class="form-control" v-model.number="evaluationData.penalty_value_nominal"
                   :disabled="!isLotSelected"/>
          </div>
          <div style="flex-basis: 150px;">
            <label>{{ $t('labels.common.defect_uom') }}</label>
            <multiselect v-model="evaluationData.penalty_measure" :max-height="200"
                         placeholder="" :options="measurements" :close-on-select="true" :disabled="!isLotSelected"
                         :allow-empty="false" select-label="" deselect-label="" selected-label=""
            />
          </div>
        </div>
        <div>
          <label>{{ $t('labels.common.price_per_defect') }}, {{ priceInfo }}</label>
          <input v-model.number="evaluationData.penalty" class="form-control" :disabled="!isLotSelected"/>
        </div>
        <div>
          <label for="additional_cost">{{ $t('labels.common.additional_cost') }}, {{ priceInfo }}</label>
          <input id="additional_cost" v-digits-after-decimal="{ numberOfDigits: 2 }"
                 v-model.number="evaluationData.price_difference" class="form-control"/>
        </div>
        <div>
          <label>{{ $t('labels.common.notes') }}</label>
          <input v-model.lazy="evaluationData.purchase_comment" class="form-control"/>
        </div>
      </div>
    </div>
    <div slot="modal-footer" class="w-100 text-center">
      <b-btn size="sm" variant="success" @click="save">
        {{ $t('buttons.common.save') }}
      </b-btn>
    </div>
  </b-modal>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { notificationsRenderer } from '@/helpers/renderHelpers'
import { handlerResponse } from '@/helpers/commonHelpers'

const initialData = {
  purchase_order_item: null,
  price_per_unit: 0,
  penalty_value_nominal: 0,
  penalty_measure: '%',
  penalty: 0,
  purchase_comment: '',
  price_difference: 0
}

const url = 'woodware/movement_data/factual_evaluation/'

export default {
  components: { Multiselect },
  props: {
    supplier: {
      type: [Object, Boolean],
      default: () => false
    }
  },
  data () {
    return {
      ref: 'ActualEvaluationModal',
      evaluationData: {},
      lots: [],
      relatedPackages: null
    }
  },
  methods: {
    async lotChanged () {
      const { id, ...lotData } = this.evaluationData.purchase_order_item
      this.evaluationData = {
        ...this.evaluationData,
        ...lotData
      }
    },
    async fetchActiveLots () {
      const params = {
        related_fields: this.relatedFields,
        id: this.relatedPackages.toString()
      }
      const response = await this.$http.get(url, { params }).then(handlerResponse)
      this.lots = response[this.relatedFields]
    },
    prepareSavedData () {
      const { isPackage, description_full, measure_type, currency_code, purchase_order_item, ...data } = this.evaluationData
      return {
        ...data,
        // there is a thing: we are making price and purchase_order_item null
        // then we reassigning one of them trough relatedFields for adaptivity
        price: null,
        purchase_order_item: null,
        [this.relatedFields]: purchase_order_item?.id,
        additional_price: this.evaluationData.additional_price || 0,
        packets: this.relatedPackages
      }
    },
    async save () {
      const payload = this.prepareSavedData()
      const loader = this.$loading.show()
      try {
        await this.$http.put(url, payload).then(handlerResponse)
        this.$emit('save-evaluation')
        this.$refs[this.ref].hide()
      } catch (e) {
        notificationsRenderer(e.data, 'error')
      } finally {
        loader.hide()
      }
    }
  },
  computed: {
    priceUsage () {
      return this.supplier?.price_usage
    },
    relatedFields () {
      return this.priceUsage === 'price_list' ? 'price' : 'purchase_order_item'
    },
    isNew () {
      return !this.evaluationData.id
    },
    priceInfo () {
      const lot = this.evaluationData.purchase_order_item
      if (lot) {
        const { currency_code, measure_type } = lot
        return `${currency_code}/${measure_type}`
      }
      return ''
    },
    measurements () {
      const mesurements = ['%']
      const lot = this.evaluationData.purchase_order_item
      if (lot) {
        return mesurements.concat(lot.measure_type)
      }
      return mesurements
    },
    isPackage () {
      return this.evaluationData.isPackage
    },
    title () {
      const i18n = this.$i18n
      if (this.isPackage) {
        return `${i18n.t('modal_dialogs.package_nr')} ${this.evaluationData.package_nr}`
      }
      if (this.isNew) {
        return i18n.t('modal_dialogs.actual_evaluation.create_item')
      }
      return i18n.t('modal_dialogs.actual_evaluation.edit_item')
    },
    isLotSelected () {
      return this.evaluationData.purchase_order_item
    }
  },
  mounted () {
    this.$root.$on('openActualEvaluationModal', async ({ relatedPackages, ...data }) => {
      this.relatedPackages = relatedPackages
      this.evaluationData = {}
      if (data) {
        if (data.purchase_order_item) {
          const { price_per_unit } = data.purchase_order_item
          this.evaluationData = { ...initialData, ...data, price_per_unit }
        } else if (data.price) {
          this.evaluationData = { ...initialData, ...data, purchase_order_item: data.price, price_per_unit: data.price.price_per_unit }
        }
        await this.fetchActiveLots()
      } else {
        this.evaluationData = { ...initialData, ...data }
      }
      if (this.$refs[this.ref]) {
        this.$refs[this.ref].show()
      }
    })
  },
  beforeDestroy () {
    this.$root.$off('openActualEvaluationModal')
  }
}
</script>




// WEBPACK FOOTER //
// src/components/modals/ActualEvaluationModal.vue